import { __decorate } from "tslib";
import cloneDeep from "lodash-es/cloneDeep";
import keyBy from "lodash-es/keyBy";
import { StateWatcher } from "./watcher/StateWatcher";
import { State } from 'vue-class-state';
import toArray from "lodash-es/toArray";
import { customApiPrefix } from "../templates/apis/custom";
import { hideProp } from "../../tools/hideProp";
import { getRealBodySchema, getRealQuerySchema, getRealResponseSchema } from "../common/schemaUtil";
import { NotEnumerable } from "../../tools/enumerable";
import { LazyGetter } from "../../tools/decorator/LazyGetter";
import { getActionSchema } from "./utils/getActionSchema";
import { StateMeta } from "./utils/StateMeta";
import { getDBParamsSchema } from "../common/getDBParamsSchema";
import { isJSStateModel } from "../common/getStateModels";
import { init$methods } from "../stateJS/utils/init$methods";
import { getSourceProtocolBySource } from "../stateJS/utils/protocol";
import { initCustomActions, initRuntime$Requests, initRuntime$RequestsProxy, initRuntimeActions, initSchema, initStateMeta } from "./BaseState.methods";
import Vue from 'vue';
import WujiTips from "../../tools/wujiTips";
import { getDBResponseBodySchema } from "../common/getDBResponseBodySchema";
import { defineExampleProperty } from "../stateJS/utils/defineProxyState";
import { getIsEditorMode } from "../../tools/designer/getIsEditorMode";
export class BaseState {
  /**
   * 可用action
   */
  constructor(collection, options) {
    this.isReady = false;
    this.promiseForReady = new Promise(resolve => {
      this.resolveForReady = () => {
        resolve();
        this.isReady = true;
      };
    });
    this.stateMeta = new StateMeta();
    /**
     * 原始请求方法，lessCode使用
     */
    this.$requests = {};
    /**
     * 全局loading
     */
    this.loading = false;
    /**
     * 全局error信息
     */
    this.errorMessage = null;
    this.stateWatcher = new StateWatcher();
    this.customState = {};
    this.customActionSchema = [];
    this.customStateSchema = null;
    this.$requestsSchema = {};
    this.options = options;
    if (getIsEditorMode()) {
      var _this$options$sourceC;
      const customStateField = (_this$options$sourceC = this.options.sourceConfig) === null || _this$options$sourceC === void 0 || (_this$options$sourceC = _this$options$sourceC.fields) === null || _this$options$sourceC === void 0 ? void 0 : _this$options$sourceC.find(item => item.id === 'customState');
      if (customStateField !== null && customStateField !== void 0 && customStateField['ds:useExample']) {
        defineExampleProperty(this, 'customState');
      }
    }
    if (this.sync) {
      // 同步加载的数据源，数据源的配置和内部的collection只能异步加载
      this.promiseForCollection = new Promise(resolve => {
        var _this$options$getColl, _this$options;
        (_this$options$getColl = (_this$options = this.options).getCollection) === null || _this$options$getColl === void 0 ? void 0 : _this$options$getColl.call(_this$options).then(asyncCollection => {
          // hideProp(this, 'collection', { value: asyncCollection });
          // @ts-ignore
          Vue.util.defineReactive(this, 'collection', asyncCollection, undefined, true);
          resolve();
        });
      });
    } else {
      if (collection) {
        hideProp(this, 'collection', {
          value: collection
        });
      }
      this.promiseForCollection = Promise.resolve();
    }
  }
  get cgiNames() {
    this.checkCollection();
    const templateCgiNames = this.collection.template.apiTemplates.map(item => item.name);
    const customCgiNames = Object.keys(this.collection.collectionData.cgiInfo).filter(name => name.startsWith(customApiPrefix));
    return templateCgiNames.concat(customCgiNames);
  }
  get isDB() {
    this.checkCollection();
    return ['wuji1_0', 'db', 'combineData', 'db_join'].includes(this.collection.collectionData.type);
  }
  get isHttp() {
    this.checkCollection();
    const {
      type
    } = this.collection.collectionData;
    return type === 'http' || type === 'frontEndHttp';
  }
  /**
   * 是否为新数据源模型
   */
  get isJSStateModel() {
    var _this$options$sourceC2;
    const stateModel = (_this$options$sourceC2 = this.options.sourceConfig) === null || _this$options$sourceC2 === void 0 ? void 0 : _this$options$sourceC2.stateModel;
    return typeof stateModel === 'string' && isJSStateModel(stateModel);
  }
  get sync() {
    var _this$options$extraCo, _this$options$sourceC3;
    if (!this.isJSStateModel) {
      return false;
    }
    return ((_this$options$extraCo = this.options.extraConfig) === null || _this$options$extraCo === void 0 ? void 0 : _this$options$extraCo.sync) || ((_this$options$sourceC3 = this.options.sourceConfig) === null || _this$options$sourceC3 === void 0 ? void 0 : _this$options$sourceC3.sync);
  }
  /**
   * 获取实体schema
   * @returns
   */
  getEntitySchema(options = {
    clone: false
  }) {
    this.checkCollection();
    const schema = this.collection.getEntitySchema();
    return options.clone ? cloneDeep(schema) : schema;
  }
  /**
   * 异步版本的获取实体
   * @param options
   * @returns
   */
  async getEntitySchemaAsync(options = {
    clone: false
  }) {
    await this.promiseForCollection;
    return this.getEntitySchema(options);
  }
  /**
   * 获取支持的Cgi
   * @returns
   */
  getSupportedCgis() {
    const names = this.cgiNames;
    const {
      collectionData
    } = this.collection;
    if (collectionData.type === 'combineData') {
      // DB默认支持
      return names;
    }
    return names.filter(name => typeof this.collection[name] === 'function' && collectionData.cgiInfo[name]);
  }
  getSupportedCgiMap() {
    return this.getSupportedCgis().reduce((prev, name) => {
      prev[name] = true;
      return prev;
    }, {});
  }
  /**
   * 判断cgi是否支持
   * @param cgiName
   * @returns
   */
  getCgiSupported(cgiName) {
    if (!this.cgiNames.includes(cgiName)) {
      console.error(`不支持${cgiName}的cgi`);
      return false;
    }
    return this.getSupportedCgis().includes(cgiName);
  }
  /**
   * 获取支持的action
   */
  getSupportedActions() {
    const {
      actionInfos
    } = this.stateMeta;
    const supportedCgis = this.getSupportedCgis();
    const actionNames = toArray(actionInfos).filter(info => {
      var _info$def$cgiDeps;
      if ((_info$def$cgiDeps = info.def.cgiDeps) !== null && _info$def$cgiDeps !== void 0 && _info$def$cgiDeps.length) {
        // 所有依赖的cgi支持，才认为该action是支持的
        return info.def.cgiDeps.every(name => supportedCgis.includes(name));
      }
      return true;
    }).map(info => info.actionName);
    return actionNames;
  }
  /**
   * 判断action是否支持
   * @param actionName
   * @returns
   */
  getActionSupported(actionName) {
    return this.getSupportedActions().includes(actionName);
  }
  /**
   * 执行action
   * @param actionName
   * @returns
   */
  async dispatch(actionName, ...args) {
    const {
      stateMeta: actionMeta
    } = this;
    const actionInfo = actionMeta.actionInfos[actionName];
    if (typeof this[actionInfo.methodName] === 'function') {
      return this[actionInfo.methodName](...args);
    }
    throw new Error('找不到对应的action');
  }
  initWatcher() {
    var _watcherSchema$fields;
    const {
      watcherSchema
    } = Object.getPrototypeOf(this).constructor;
    if (!watcherSchema) return;
    const {
      watcher = []
    } = this.options;
    const watcherMap = keyBy(watcher, 'id');
    const set = (enabled, id) => {
      if (enabled) {
        this.stateWatcher.enable(id);
      } else {
        this.stateWatcher.disable(id);
      }
    };
    (_watcherSchema$fields = watcherSchema.fields) === null || _watcherSchema$fields === void 0 ? void 0 : _watcherSchema$fields.forEach(item => {
      const {
        id
      } = item;
      if (!id || typeof id === 'number') return;
      const customOption = watcherMap[id];
      const enabled = customOption ? customOption === null || customOption === void 0 ? void 0 : customOption.enabled : item.default;
      set(enabled, id);
    });
  }
  getCgiParamSchema(name) {
    this.checkCollection();
    const {
      collection: {
        collectionData: {
          cgiInfo
        }
      },
      isDB
    } = this;
    const paramSchema = {
      id: 'params',
      type: 'object',
      fields: [],
      sourceRequestParamsType: isDB ? 'db' : 'http'
    };
    const info = cgiInfo[name];
    if (!info) return paramSchema;
    if (isDB) {
      // DB请求结构的
      // 从模板取
      const entity = this.getEntitySchema();
      const schema = cloneDeep(getDBParamsSchema(name, entity));
      paramSchema.fields = schema.fields;
    } else {
      var _paramSchema$fields;
      const query = getRealQuerySchema(info);
      (_paramSchema$fields = paramSchema.fields) === null || _paramSchema$fields === void 0 ? void 0 : _paramSchema$fields.push({
        ...cloneDeep(query),
        id: 'query',
        type: 'object'
      });
      if (info.type === 'post') {
        var _paramSchema$fields2;
        const body = getRealBodySchema(info);
        (_paramSchema$fields2 = paramSchema.fields) === null || _paramSchema$fields2 === void 0 ? void 0 : _paramSchema$fields2.push({
          ...cloneDeep(body),
          id: 'body',
          type: 'object'
        });
      }
    }
    return paramSchema;
  }
  getCgiResponseSchema(name) {
    this.checkCollection();
    const {
      collection: {
        collectionData: {
          cgiInfo
        }
      },
      isDB
    } = this;
    const info = cgiInfo[name];
    if (!info) return null;
    const bodySchema = {
      id: 'body'
    };
    const responseBodySchema = isDB ? getDBResponseBodySchema(name, this.getEntitySchema({
      clone: true
    })) : getRealResponseSchema(info);
    Object.assign(bodySchema, responseBodySchema);
    bodySchema.id = 'body';
    bodySchema.title = '返回包体';
    const responseSchema = {
      type: 'object',
      fields: [{
        id: 'headers',
        type: 'object',
        title: 'HTTP返回头'
      }, bodySchema]
    };
    return responseSchema;
  }
  /**
   * 必须子类调用
   */
  async initState() {
    var _this$registerWatcher;
    const {
      isJSStateModel
    } = this;
    if (!isJSStateModel) {
      initCustomActions.apply(this);
    }
    initStateMeta.apply(this);
    // 这里有先后顺序要求
    if (!isJSStateModel) {
      initRuntimeActions.apply(this);
    }
    initSchema.apply(this); // 不能加 await ，就是要走同步初始化
    if (this.sync) {
      initRuntime$RequestsProxy.apply(this);
    } else {
      initRuntime$Requests.apply(this);
    }
    (_this$registerWatcher = this.registerWatcher) === null || _this$registerWatcher === void 0 ? void 0 : _this$registerWatcher.call(this);
    init$methods(this); // 这个无关异步
  }
  /**
   * 等待数据源就绪。
   *
   * 这是一个内置基础方法
   *
   * @see {stateSystemMethods}
   */
  waitForReady() {
    return this.promiseForReady;
  }
  /**
   * 获取code和message 优先标准的code，message，不然读取
   * @param body
   * @returns
   */
  getCodeAndMsg(body) {
    const defaultCode = body[defaultCodeKey];
    const defaultMsg = body[defaultMsgKey];
    if (this.isDB) {
      return {
        code: defaultCode,
        message: defaultMsg
      };
    }
    const {
      codeKey,
      msgKey
    } = this.options.extraConfig || {};
    const customCode = codeKey && typeof body[codeKey] === 'number' ? body[codeKey] : null;
    const customMsg = msgKey && typeof body[msgKey] === 'string' ? body[msgKey] : null;
    return {
      code: typeof defaultCode === 'number' ? defaultCode : customCode,
      message: typeof defaultMsg === 'string' ? defaultMsg : customMsg
    };
  }
  checkSucc(body, options) {
    const {
      code,
      message
    } = this.getCodeAndMsg(body);
    const hasCode = typeof code === 'number';
    const hasMessage = typeof message === 'string';
    const isSuccess = hasCode ? code === 0 : true;
    if (isSuccess) {
      var _options$success;
      (_options$success = options.success) === null || _options$success === void 0 ? void 0 : _options$success.call(options);
    } else {
      var _options$fail;
      (_options$fail = options.fail) === null || _options$fail === void 0 ? void 0 : _options$fail.call(options);
    }
    let fullMsg = '';
    if (!isSuccess) {
      const msg = hasMessage ? message : '';
      const {
        disableMsgPrefix
      } = this.options.extraConfig || {};
      fullMsg = disableMsgPrefix ? msg : `【${options.cgiName}】接口请求失败 错误码:${code} \n错误消息:${msg}`;
      console.error(fullMsg);
      if (options.showError) {
        const wujiTips = new WujiTips();
        wujiTips.error({
          content: fullMsg,
          duration: 3
        });
      }
    }
    return {
      success: isSuccess,
      message: fullMsg,
      data: body
    };
  }
  async init() {
    // empty
  }
  checkCollection() {
    if (!this.sync) {
      return;
    }
    if (!this.collection) {
      const {
        id
      } = this.options.sourceConfig || {};
      throw new Error(`数据源【${id}】配置未拉取完毕。`);
    }
  }
}
__decorate([State], BaseState.prototype, "isReady", void 0);
__decorate([LazyGetter(getActionSchema)], BaseState.prototype, "actionSchema", void 0);
__decorate([NotEnumerable], BaseState.prototype, "stateMeta", void 0);
__decorate([NotEnumerable], BaseState.prototype, "$requests", void 0);
__decorate([NotEnumerable], BaseState.prototype, "options", void 0);
__decorate([LazyGetter(function () {
  this.checkCollection();
  const protocol = getSourceProtocolBySource(this);
  return protocol;
})], BaseState.prototype, "protocol", void 0);
__decorate([State], BaseState.prototype, "loading", void 0);
__decorate([State], BaseState.prototype, "errorMessage", void 0);
__decorate([NotEnumerable], BaseState.prototype, "stateWatcher", void 0);
__decorate([State], BaseState.prototype, "customState", void 0);
__decorate([NotEnumerable], BaseState.prototype, "customActionSchema", void 0);
__decorate([NotEnumerable], BaseState.prototype, "customStateSchema", void 0);
__decorate([NotEnumerable], BaseState.prototype, "$requestsSchema", void 0);
const defaultCodeKey = 'code';
const defaultMsgKey = 'message';
//# sourceMappingURL=BaseState.js.map
